import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * TELEFON KABUĞU YARDIMCILARI
 *
 *  · ekran genişliği kancaları (telefon ≤640, dar ≤900)
 *  · KATMAN YÖNETİMİ: açık çekmece/pencere varken gövde kaymaz; telefonun geri tuşu
 *    (ya da kenardan geri hareketi) sayfadan çıkarmaz, en üstteki katmanı kapatır
 *  · klavye payı: ekran klavyesi açıkken yapışkan alt çubuklar klavyenin üstünde kalır
 *  · kaydırma konumu: listeye geri dönünce kalınan yerden devam edilir
 */
export const TELEFON = '(max-width: 640px)';
export const DAR = '(max-width: 900px)';

export function useMedya(sorgu: string): boolean {
  const [uyar, setUyar] = useState(() => window.matchMedia(sorgu).matches);
  useEffect(() => {
    const m = window.matchMedia(sorgu);
    const degis = () => setUyar(m.matches);
    degis();
    m.addEventListener('change', degis);
    return () => m.removeEventListener('change', degis);
  }, [sorgu]);
  return uyar;
}
export const useTelefon = () => useMedya(TELEFON);
export const useDar = () => useMedya(DAR);

/* ─── Gövde kaydırma kilidi ───────────────────────────────────────────────────
   iOS Safari `overflow: hidden`'ı gövdede güvenilir uygulamaz; gövde bulunduğu yerde
   sabitlenir, kilit açılınca aynı konuma dönülür. Sayaçlıdır: iç içe katmanlarda
   son katman kapanmadan kilit açılmaz. */
let kilitSayisi = 0;
let kilitY = 0;
function kilitle() {
  kilitSayisi += 1;
  if (kilitSayisi > 1) return;
  kilitY = window.scrollY;
  const g = document.body.style;
  g.position = 'fixed'; g.top = `-${kilitY}px`; g.left = '0'; g.right = '0'; g.width = '100%'; g.overflow = 'hidden';
  document.documentElement.setAttribute('data-katman', '1');
  altPayYaz();
}
function kilidiAc() {
  if (kilitSayisi === 0) return;
  kilitSayisi -= 1;
  if (kilitSayisi > 0) return;
  const g = document.body.style;
  g.position = ''; g.top = ''; g.left = ''; g.right = ''; g.width = ''; g.overflow = '';
  document.documentElement.removeAttribute('data-katman');
  const hedef = kilitY;
  window.scrollTo(0, hedef);
  altPayYaz();
  /* Safari (WebKit), katman geri tuşuyla kapandığında geçmiş kaydının "kaydırma konumunu" kendisi geri
     yüklemeye çalışır ve sayfayı en alta atar (`scrollRestoration = 'manual'` iken de; WebKit'te sınandı).
     Kısa bir süre konum korunur; kullanıcı dokunur ya da tekerleği çevirirse hemen bırakılır. */
  let deneme = 0;
  const koru = () => { if (kilitSayisi === 0 && Math.abs(window.scrollY - hedef) > 1 && deneme++ < 6) window.scrollTo(0, hedef); };
  const birak = () => { window.removeEventListener('scroll', koru); window.removeEventListener('touchstart', birak); window.removeEventListener('wheel', birak); };
  window.addEventListener('scroll', koru, { passive: true });
  window.addEventListener('touchstart', birak, { passive: true, once: true });
  window.addEventListener('wheel', birak, { passive: true, once: true });
  window.setTimeout(birak, 500);
}
/**
 * Sabit duran alt öğelerin (Sorun Bildir «?» düğmesi, alt bildirim çubuğu) alt boşluğu.
 * Bu öğeler gölge kökte (shadow DOM) yaşar; kabuğun CSS'i onlara ulaşmaz ama CSS değişkeni ulaşır.
 *  · dar ekran: alt gezinme çubuğunun (ve varsa sayfanın sabit eylem çubuğunun) üstünde durur
 *  · telefonda tam ekran katman açıkken: gizlenir (`--am-sb-gorunum`)
 */
let darEkran = false;
let telefonEkran = false;
let eylemCubuguPayi = 0;
let menuAcik = false;
function altPayYaz() {
  const k = document.documentElement.style;
  const pay = darEkran ? `calc(${58 + (telefonEkran ? eylemCubuguPayi : 0)}px + env(safe-area-inset-bottom, 0px))` : '0px';
  k.setProperty('--am-alt-pay', pay);
  // Telefonda tam ekran katman (çekmece, alt sayfa) ya da sol menü açıkken yüzen «?» düğmesi gizlenir:
  // formun alanlarına ve yapışkan eylem düğmesine binmesin. Katman kapanınca geri gelir.
  if ((kilitSayisi > 0 && telefonEkran) || menuAcik) k.setProperty('--am-sb-gorunum', 'hidden'); else k.removeProperty('--am-sb-gorunum');
}
export function altPayAyarla(dar: boolean, telefon: boolean) { darEkran = dar; telefonEkran = telefon; altPayYaz(); }
/** Sol menü çekmecesi / ⋯ menüsü açıkken yüzen «?» düğmesi gizlenir (menünün alt satırına binmesin). */
export function sorunBildirGizle(gizle: boolean) { menuAcik = gizle; altPayYaz(); }

/** Gövde kilitliyken sayfanın gerçek kaydırma konumu. */
export const sayfaY = () => (kilitSayisi > 0 ? kilitY : window.scrollY);

/* ─── Katman yığını + geri tuşu ───────────────────────────────────────────────
   Her açılan katman için geçmişe AYNI adresle bir kayıt eklenir (react-router'ın
   kendi durumu kopyalanır: adres ve anahtar değişmediği için sayfa yeniden çizilmez).
   Geri → kayıt çıkar → en üstteki katman kapanır. Katman arayüzden kapanırsa kendi
   kaydını geri alır; böylece geçmişte artık kayıt kalmaz. */
type Kayit = { id: number; kapat: () => void };
const yigin: Kayit[] = [];
let sayac = 0;
let yoksay = 0;
let dinleniyor = false;

const bizimKayit = () => typeof (window.history.state as { amKatman?: unknown } | null)?.amKatman === 'number';

function geriGeldi() {
  if (yoksay > 0) { yoksay -= 1; return; }
  const ust = yigin.pop();
  if (ust) {
    kilidiAc();
    try { ust.kapat(); } catch { /* katman zaten gitmiş */ }
    return;
  }
  // Sahibi kalmamış kayıt (katman açıkken başka sayfaya gidilmişti): kullanıcıya boş bir "geri" yaşatma.
  if (bizimKayit()) { yoksay += 1; window.history.back(); }
}

export function katmanAc(kapat: () => void): number {
  if (!dinleniyor) { window.addEventListener('popstate', geriGeldi); dinleniyor = true; }
  const id = ++sayac;
  yigin.push({ id, kapat });
  kilitle();
  try { window.history.pushState({ ...(window.history.state ?? {}), amKatman: id }, ''); } catch { /* geçmiş kapalı */ }
  return id;
}

/** Katman arayüzden kapandı (Kapat düğmesi, kaydet, perdeye dokunma). */
export function katmanBirak(id: number) {
  const i = yigin.findIndex((k) => k.id === id);
  if (i < 0) return; // geri tuşuyla zaten çıkarıldı
  yigin.splice(i, 1);
  kilidiAc();
  // Karar bir tur ertelenir: "kapat, sonra başka sayfaya git" sırasında yeni sayfanın kaydı geri alınmasın.
  window.setTimeout(() => { if (bizimKayit()) { yoksay += 1; window.history.back(); } }, 0);
}

/** Kendi durumunu tutan katmanlar için: `acik` iken geri tuşu `kapat`'ı çağırır. */
export function useGeriKapat(acik: boolean, kapat: () => void) {
  const k = useRef(kapat);
  k.current = kapat;
  useEffect(() => {
    if (!acik) return;
    const id = katmanAc(() => k.current());
    return () => katmanBirak(id);
  }, [acik]);
}

/**
 * Sayfalardaki çekmeceler ortak sınıfları kullanır; hepsini tek tek kancaya bağlamak yerine
 * DOM izlenir: perde eklenince katman açılmış, kaldırılınca kapanmış sayılır. Geri tuşunda
 * perdeye dokunulmuş gibi davranılır (her çekmece perdesine dokununca kapanır; meşgulse
 * kapanmaz ve kayıt yeniden eklenir).
 */
const PERDE = '.ccd-drawer-arka, .ccd-site-perde, .ccd-uk-buyuk, .ccd-destek-buyuk';
/** DOM her değiştiğinde (kare başına en çok bir kez) çağrılacak ek işler: tablo → kart etiketleme. */
const domIsleri = new Set<() => void>();
export function domIsiEkle(f: () => void): () => void { domIsleri.add(f); return () => { domIsleri.delete(f); }; }
function perdeyeDokun(el: Element) {
  for (const tur of ['mousedown', 'mouseup', 'click']) el.dispatchEvent(new MouseEvent(tur, { bubbles: true, cancelable: true, view: window }));
}
export function useCekmeceIzleyici() {
  useEffect(() => {
    const acik = new Map<Element, number>();
    let bekliyor = false;
    const tara = () => {
      bekliyor = false;
      for (const f of domIsleri) { try { f(); } catch { /* yardımcı iş kabuğu düşürmez */ } }
      const simdi = new Set(document.querySelectorAll(PERDE));
      for (const [el, id] of acik) if (!simdi.has(el)) { acik.delete(el); katmanBirak(id); }
      for (const el of simdi) {
        if (acik.has(el)) continue;
        const id = katmanAc(() => {
          acik.delete(el);
          perdeyeDokun(el);
          // Kapanmadıysa (kayıt sürüyor) bir sonraki taramada yeniden kaydedilir.
          window.setTimeout(planla, 60);
        });
        acik.set(el, id);
      }
    };
    const planla = () => { if (!bekliyor) { bekliyor = true; window.requestAnimationFrame(tara); } };
    const g = new MutationObserver(planla);
    g.observe(document.body, { childList: true, subtree: true });
    const m = window.matchMedia(TELEFON);
    m.addEventListener('change', planla);
    planla();
    return () => { g.disconnect(); m.removeEventListener('change', planla); for (const id of acik.values()) katmanBirak(id); acik.clear(); };
  }, []);
}

/* ─── Klavye payı ─────────────────────────────────────────────────────────────
   Ekran klavyesi yalnız GÖRSEL görünümü küçültür (iOS ve Chrome 108+): `100dvh` ve
   `bottom: 0` klavyenin altında kalır. Klavyenin kapladığı yükseklik `--ccd-klavye`
   değişkenine yazılır; tam ekran katmanlar ve yapışkan alt çubuklar bunu düşer.
   Odaklanan alan klavyenin üstünde kalacak biçimde kaydırılır. */
export function useKlavyePayi() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const kok = document.documentElement;
    let son = -1;
    const olc = () => {
      const pay = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      // 80 pikselden küçük farklar adres çubuğunun oynamasıdır, klavye değil.
      const deger = pay > 80 ? pay : 0;
      if (deger === son) return;
      son = deger;
      kok.style.setProperty('--ccd-klavye', `${deger}px`);
      if (deger > 0) kok.setAttribute('data-klavye', '1'); else kok.removeAttribute('data-klavye');
      if (deger > 0) gorunurYap();
    };
    const gorunurYap = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      // Yalnız dokunmatik dar ekranda: masaüstünde odak sayfayı oynatmaz.
      if (!window.matchMedia(DAR).matches || !window.matchMedia('(pointer: coarse)').matches) return;
      window.setTimeout(() => {
        const r = el.getBoundingClientRect();
        const alt = vv.offsetTop + vv.height - 76; // yapışkan eylem çubuğu payı
        // Altında öneri listesi açılan alanlar (`data-klavye-ust`) ekranın üstüne alınır: liste klavyenin üstünde kalır.
        if (el.closest('[data-klavye-ust]')) { el.scrollIntoView({ block: 'start', behavior: 'smooth' }); return; }
        if (r.bottom > alt || r.top < vv.offsetTop + 56) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 60);
    };
    vv.addEventListener('resize', olc);
    vv.addEventListener('scroll', olc);
    document.addEventListener('focusin', gorunurYap);
    olc();
    return () => { vv.removeEventListener('resize', olc); vv.removeEventListener('scroll', olc); document.removeEventListener('focusin', gorunurYap); kok.style.removeProperty('--ccd-klavye'); kok.removeAttribute('data-klavye'); };
  }, []);
}

/* ─── Kaydırma konumu ─────────────────────────────────────────────────────────
   İleri gidişte sayfa en üstten açılır; GERİ dönüşte (telefonun geri tuşu, ‹ düğmesi)
   liste kaldığı yere iner. Veri gelene kadar sayfa kısa olabilir: yükseklik yetene
   kadar (en çok 3 sn) yeniden denenir; kullanıcı kendisi kaydırırsa bırakılır. */
const KONUM = 'am_kaydirma';
function konumlar(): Record<string, number> {
  try { return JSON.parse(sessionStorage.getItem(KONUM) ?? '{}') as Record<string, number>; } catch { return {}; }
}
export function useKaydirmaHatirla() {
  const konum = useLocation();
  const tur = useNavigationType();
  const anahtar = konum.key;
  const oncekiYol = useRef<string | null>(null);
  useEffect(() => {
    try { window.history.scrollRestoration = 'manual'; } catch { /* eski tarayıcı */ }
  }, []);
  useEffect(() => {
    const hedef = tur === 'POP' ? konumlar()[anahtar] : undefined;
    let birak = false;
    let z = 0;
    if (konum.hash) { /* `#bölüm` ile gelindi: kabuk öğeye iner */ }
    else if (hedef && hedef > 0) {
      const bas = Date.now();
      const dokundu = () => { birak = true; };
      window.addEventListener('touchstart', dokundu, { passive: true, once: true });
      window.addEventListener('wheel', dokundu, { passive: true, once: true });
      const dene = () => {
        if (birak) return;
        const enCok = document.documentElement.scrollHeight - window.innerHeight;
        if (enCok >= hedef - 4) { window.scrollTo(0, hedef); return; }
        window.scrollTo(0, Math.max(0, enCok));
        if (Date.now() - bas < 3000) z = window.setTimeout(dene, 80);
      };
      dene();
    } else if (tur !== 'POP' && oncekiYol.current !== null && oncekiYol.current !== konum.pathname) window.scrollTo(0, 0);
    // Aynı sayfada sekme / dönem / süzgeç değişimi (yalnız sorgu dizgisi) konumu bozmaz.
    oncekiYol.current = konum.pathname;

    // Ayrılırken (ve kaydırdıkça) bu geçmiş kaydının konumu saklanır.
    let bekle = 0;
    let sonY = Math.round(sayfaY());
    const yaz = () => {
      const k = konumlar();
      k[anahtar] = sonY;
      const adlar = Object.keys(k);
      if (adlar.length > 60) for (const a of adlar.slice(0, adlar.length - 60)) delete k[a];
      try { sessionStorage.setItem(KONUM, JSON.stringify(k)); } catch { /* özel pencere */ }
    };
    const kaydi = () => { if (!document.documentElement.hasAttribute('data-katman')) sonY = Math.round(window.scrollY); window.clearTimeout(bekle); bekle = window.setTimeout(yaz, 120); };
    window.addEventListener('scroll', kaydi, { passive: true });
    return () => { birak = true; window.clearTimeout(z); window.clearTimeout(bekle); window.removeEventListener('scroll', kaydi); yaz(); };
  }, [anahtar]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Telefonda sayfanın altında sabit duran eylem çubuğu (Kaydet · PDF, toplam) varken
 * yüzen «?» düğmesi onun da üstüne çıkar. Çubuğu çizen sayfa bu kancayı çağırır.
 */
export function useEylemCubugu(acik: boolean, yukseklik = 68) {
  useEffect(() => {
    if (!acik) return;
    eylemCubuguPayi += yukseklik; altPayYaz();
    return () => { eylemCubuguPayi -= yukseklik; altPayYaz(); };
  }, [acik, yukseklik]);
}

/**
 * Dosyayı telefonun paylaşım sayfasına verir (Web Share API, dosya paylaşımı).
 * Yalnız dokunmatik dar ekranda denenir: masaüstünde "indir" davranışı değişmez.
 *  · 'paylasildi' / 'vazgecti': paylaşım sayfası açıldı; indirme başlatılmaz
 *  · 'izin': tarayıcı "kullanıcı dokunuşu" istiyor (iOS: dosya sunucudan gelene kadar dokunuşun süresi doldu) —
 *            çağıran, hazır dosya için bir "Paylaş" düğmesi gösterip yeniden çağırmalı
 *  · 'yok': desteklenmiyor; çağıran indirsin
 */
export type PaylasimSonucu = 'paylasildi' | 'vazgecti' | 'izin' | 'yok';
export function paylasilabilir(): boolean {
  const gezgin = navigator as Navigator & { canShare?: (v: ShareData) => boolean };
  return window.matchMedia(DAR).matches && window.matchMedia('(pointer: coarse)').matches
    && typeof gezgin.share === 'function' && typeof gezgin.canShare === 'function';
}
export async function dosyaPaylas(blob: Blob, ad: string, baslik?: string): Promise<PaylasimSonucu> {
  if (!paylasilabilir()) return 'yok';
  const dosya = new File([blob], ad, { type: blob.type || 'application/pdf' });
  if (!navigator.canShare({ files: [dosya] })) return 'yok';
  try { await navigator.share({ files: [dosya], title: baslik ?? ad }); return 'paylasildi'; }
  catch (e) {
    const adi = (e as DOMException)?.name;
    return adi === 'AbortError' ? 'vazgecti' : adi === 'NotAllowedError' ? 'izin' : 'yok';
  }
}
